// CalibrationLogBookForm.js
// Host for a single standalone Calibration Log Book. Wires the shared CalibrationLogSection to the
// active book store + the book autosave hook, and provides Back / Complete (PDF) actions.

import React, { useEffect, useMemo, useState } from "react";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../../../store/appStore";
import { useUserState } from "../../../../context/user";
import { useCalibrationLogBookAutosave } from "../../../../hooks/useCalibrationLogBookAutosave";
import { CalibrationLogSection } from "../../../shared/calibrationLog/CalibrationLogSection";
import { CalibrationLogBookInfo } from "./CalibrationLogBookInfo";
import { AutosaveIndicator } from "../../../common/AutosaveIndicator";
import { getSamplingEquipmentMeters } from "../../../../api/SamplingEventsApi";
import { completeCalibrationLogBook } from "../../../../api/CalibrationLogsApi";
import {
  getTaskStatus,
  downloadTaskResult,
} from "../../../../api/TasksApi";
import styles from "./CalibrationLogBookForm.module.css";

export const CalibrationLogBookForm = ({ onBack }) => {
  const user = useUserState().currentUser;

  // Subscribe to the active book + revision so the section re-renders on mutation.
  const activeCalibrationLogBook = useAppStore(
    (s) => s.activeCalibrationLogBook,
    shallow
  );
  const book = activeCalibrationLogBook?.currentBook;
  const bookId = useMemo(() => book?.id || null, [book?.id]);

  const clActions = useAppStore(
    (s) =>
      s.activeCalibrationLogBookActions?.calibrationLogBookFormActions
        ?.calibrationLogs,
    shallow
  );
  const calibrationLogUI =
    activeCalibrationLogBook?.calibrationLogBookForm?.calibrationLogUI || {};

  const showNotification = useAppStore(
    (s) => s.notificationActions.showNotification
  );

  // Autosave (socket + offline queue)
  const { emitPatch, connectionStatus, saveStatus, pendingCount } =
    useCalibrationLogBookAutosave(bookId);

  // Equipment meters (reused endpoint)
  const [meters, setMeters] = useState([]);
  useEffect(() => {
    let didCancel = false;
    (async () => {
      try {
        const res = await getSamplingEquipmentMeters();
        const list = res?.data?.sampling_equipment_meters || [];
        if (!didCancel) setMeters(list);
      } catch {
        if (!didCancel) setMeters([]);
      }
    })();
    return () => {
      didCancel = true;
    };
  }, []);

  const [completing, setCompleting] = useState(false);

  const handleComplete = async () => {
    if (!bookId || completing) return;
    setCompleting(true);
    try {
      const res = await completeCalibrationLogBook(bookId);
      const taskId = res?.data?.task_id;
      showNotification({
        type: "success",
        title: "Calibration Log Book Completed",
        message: taskId
          ? "Generating the PDF report..."
          : "Book marked complete.",
      });

      // Poll for the PDF if a task was enqueued
      if (taskId) {
        let attempts = 0;
        let lastStatus = null;
        let ready = false;
        while (!ready && attempts < 60) {
          await new Promise((r) => setTimeout(r, 2000));
          const { data: status } = await getTaskStatus(taskId);
          lastStatus = status;
          ready = !!status?.ready;
          if (ready && !status?.ok) break;
          attempts += 1;
        }
        if (ready && lastStatus?.ok) {
          const dl = await downloadTaskResult(taskId, {
            s3DownloadUrl: lastStatus?.s3_download_url,
            filename: lastStatus?.filename,
          });
          if (dl) {
            const blob = new Blob([dl.data], {
              type: dl.headers["content-type"] || "application/pdf",
            });
            const cd = dl.headers["content-disposition"] || "";
            const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
            const filename = match
              ? decodeURIComponent(match[1])
              : "calibration_log_book.pdf";
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          }
        }
      }

      if (onBack) onBack();
    } catch (e) {
      showNotification({
        type: "error",
        title: "Failed to complete calibration log book",
        message: e.message || "Unknown error",
      });
    } finally {
      setCompleting(false);
    }
  };

  if (!book) {
    return (
      <div className={styles.formContainer}>
        <p>Loading calibration log book...</p>
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <button className={styles.backButton} onClick={onBack}>
          ← Back
        </button>
        <div className={styles.formTitle}>
          {book.bookName || "Calibration Log Book"}
        </div>
        <div className={styles.statusArea}>
          <AutosaveIndicator
            connectionStatus={connectionStatus}
            saveStatus={saveStatus}
            pendingCount={pendingCount}
          />
          {!book.completed && (
            <button
              className={styles.completeButton}
              onClick={handleComplete}
              disabled={completing}
            >
              {completing ? "Completing..." : "Complete"}
            </button>
          )}
        </div>
      </div>

      <div className={styles.formBody}>
        <CalibrationLogBookInfo book={book} emitPatch={emitPatch} />
        <CalibrationLogSection
          getSortedLogs={() => book.sortEntries()}
          ui={calibrationLogUI}
          clActions={clActions}
          emitPatch={emitPatch}
          user={user}
          meters={meters}
          getExistingLogById={(id) => book.getEntryById(id)}
          parentData={book}
          sectionTitle="Calibration Log Book"
        />
      </div>
    </div>
  );
};
